import React, { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { CommonActions } from "@react-navigation/native";
import { AppScreens } from "../../screens/Utility/AppScreens";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { NotificationBadge } from "../../Components/NotificationBadge";
import { useNotificationCount } from "../../hooks/useNotificationCount";
import { useNotifications } from "../../context/NotificationContext";
import { getLoginData } from "../../utility/AppStoreData";
import { useChat } from "../../context/ChatContext";
import useAdminStatus from "../../utility/hooks/useAdminStatus";
import { AuthProvider } from "../../context/AuthContext";
import SidebarTopWalkthrough from "../../Components/walk-through-common-components/homeScreenTooltips/SidebarTopWalkthrough";
import SidebarBottomWalkthrough from "../../Components/walk-through-common-components/homeScreenTooltips/SidebarBottomWalkthrough";
import { navigateToScreen } from "../../utility/NavigationHelper";

export default function CustomDrawerContent({ navigation }) {
  const {
    setNewNotificationCount,
    notificationCounts,
    refreshNotificationCounts,
    isOnNotificationScreen,
  } = useNotifications();
  const { setChatNotificationCount, directMessageCount, setDirectMessageCount } = useChat();
  const { chatCount, resetChatCount } = useNotificationCount();
  const [activeTab, setActiveTab] = useState("HomeTabStack");
  const [isOnChatScreen, setIsOnChatScreen] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const isAdmin = useAdminStatus();

  const totalNotificationCount = notificationCounts.total;

  // Get access token on component mount
  useEffect(() => {
    const getAccessToken = async () => {
      const loginData = await getLoginData();
      if (loginData?.AccessToken) {
        setAccessToken(loginData.AccessToken);
      }
    };
    getAccessToken();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentUrl = window.location.href;
      setIsOnChatScreen(currentUrl.includes("/messages"));
    }, 1000);

    return () => clearTimeout(timeout);
  }, [window.location.href]);

  // Update active tab based on current URL
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Map URLs to tab stack names
    if (currentPath.startsWith('/dashboard') || currentPath === '/') {
      setActiveTab('HomeTabStack');
    } else if (currentPath.startsWith('/events')) {
      setActiveTab('MyEventsTabStack');
    } else if (currentPath.startsWith('/messages')) {
      setActiveTab('ChatTabStack');
    } else if (currentPath.startsWith('/settings')) {
      setActiveTab('SettingTabStack');
    } else if (currentPath.startsWith('/help')) {
      setActiveTab('HelpTabStack');
    } else if (currentPath.startsWith('/notifications')) {
      setActiveTab('NotificationTabStack');
    }
  }, [window.location.pathname]);

  const handleNavigateToNotifications = async () => {
    setNewNotificationCount(0);

    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: AppScreens.NotificationMain }],
      })
    );

    if (refreshNotificationCounts) {
      setTimeout(() => refreshNotificationCounts(), 500);
    }
  };

  const handleNavigateToChat = async () => {
    await resetChatCount();
    setChatNotificationCount(0);
    setDirectMessageCount(0);
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "ChatTabStack" }],
      })
    );
  };

  const handleTabPress = (tabName) => {
    setActiveTab(tabName);
  };

  const isActiveTab = (tabName) => activeTab === tabName;

  const getButtonStyle = (tabName) =>
    isActiveTab(tabName)
      ? {
          borderLeftWidth: 4,
          borderLeftColor: 'white',
        }
      : {};

  const renderButtonContent = (
    tabName,
    icon,
    title,
    showNotificationBadge = false,
    notificationCount = 0
  ) => {
    const isActive = isActiveTab(tabName);

    const iconElement = showNotificationBadge ? (
      <View>
        <Image
          source={icon}
          style={{ width: 24, height: 24, resizeMode: "contain" }}
        />
        <NotificationBadge
          count={notificationCount}
          size="small"
          position="topRight"
          isVisible={notificationCount > 0}
        />
      </View>
    ) : (
      <Image
        source={icon}
        style={{ width: 24, height: 24, resizeMode: "contain" }}
      />
    );

    const content = (
      <View
        style={{
          flexDirection: "row",
          gap: 10,
          alignItems: "center",
          paddingVertical: 8,
          paddingHorizontal: 20,
        }}
      >
        {iconElement}
        <Text style={[AppFontStyle.BOLD_ANYBODY_16]}>{title}</Text>
      </View>
    );

    if (isActive) {
      return (
        <LinearGradient
          colors={[AppColors.buttonRed_DC2626, AppColors.buttonRedDark_991B1B]}
          start={[0, 0]}
          end={[1, 0]}
        >
          {content}
        </LinearGradient>
      );
    }

    return content;
  };

  const resetNavigation = (routeName) => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: routeName }],
      })
    );
  };

  if (isAdmin) {
    return (
      <AuthProvider>
        <View
          style={{
            backgroundColor: AppColors.sidebarBackground_111827,
            width: 200,
            height: "100%",
          }}
        >
          <Image
            source={AppImages.KO_LOGO_Main}
            style={{
              width: 114,
              height: 47,
              marginTop: 20,
              resizeMode: "contain",
              alignSelf: "center",
            }}
          />
          <TouchableOpacity
            activeOpacity={0.5}
            style={[{ marginTop: 25 }, getButtonStyle("DisputeTabStack")]}
            onPress={() => {
              handleTabPress("DisputeTabStack");
              resetNavigation("DisputeTabStack");
            }}
          >
            {renderButtonContent(
              "DisputeTabStack",
              AppImages.icHome,
              "Disputes"
            )}
          </TouchableOpacity>
        </View>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <View
        style={{
          backgroundColor: AppColors.sidebarBackground_111827,
          width: 200,
          height: "100%",
        }}
      >
        <Image
          source={AppImages.KO_LOGO_Main}
          style={{
            width: 114,
            height: 47,
            marginTop: 20,
            resizeMode: "contain",
            alignSelf: "center",
          }}
        />
        <SidebarTopWalkthrough
          content={
            <View
              style={{
                flex: 1,
                minWidth: 200,
                flexDirection: "column",
                backgroundColor: AppColors.sidebarBackground_111827,
              }}
            >
              <TouchableOpacity
                activeOpacity={0.5}
                style={[{ marginTop: 25 }, getButtonStyle("HomeTabStack")]}
                onPress={() => {
                  handleTabPress("HomeTabStack");
                  resetNavigation("HomeTabStack");
                }}
              >
                {renderButtonContent("HomeTabStack", AppImages.icHome, "Home")}
              </TouchableOpacity>

              <TouchableOpacity
                activeOpacity={0.5}
                style={[{ marginTop: 5 }, getButtonStyle("MyEventsTabStack")]}
                onPress={() => {
                  handleTabPress("MyEventsTabStack");
                  resetNavigation("MyEventsTabStack");
                }}
              >
                {renderButtonContent(
                  "MyEventsTabStack",
                  AppImages.icTV,
                  "My events"
                )}
              </TouchableOpacity>

              <TouchableOpacity
                activeOpacity={0.5}
                style={[{ marginTop: 5 }, getButtonStyle("ChatTabStack")]}
                onPress={() => {
                  handleTabPress("ChatTabStack");
                  handleNavigateToChat();
                }}
              >
                {renderButtonContent(
                  "ChatTabStack",
                  AppImages.icChat,
                  "Chat",
                  !isOnChatScreen,
                  directMessageCount
                )}
              </TouchableOpacity>
            </View>
          }
        />
        <SidebarBottomWalkthrough
          content={
            <View
              style={{
                flex: 1,
                minWidth: 200,
                flexDirection: "column",
                justifyContent: "flex-end",
                backgroundColor: AppColors.sidebarBackground_111827,
              }}
            >
              <TouchableOpacity
                activeOpacity={0.5}
                style={[{ marginTop: 5 }, getButtonStyle("HelpTabStack")]}
                onPress={() => {
                  handleTabPress("HelpTabStack");
                  navigateToScreen(navigation, AppScreens.HelpScreen);
                }}
              >
                {renderButtonContent("HelpTabStack", AppImages.icHelp, "Help")}
              </TouchableOpacity>

              <TouchableOpacity
                activeOpacity={0.5}
                style={[getButtonStyle("SettingTabStack")]}
                onPress={() => {
                  handleTabPress("SettingTabStack");
                  navigateToScreen(navigation, AppScreens.SettingWebScreen);
                }}
              >
                {renderButtonContent(
                  "SettingTabStack",
                  AppImages.icSetting,
                  "Settings"
                )}
              </TouchableOpacity>

              <TouchableOpacity
                activeOpacity={0.5}
                style={[
                  { marginTop: 5, marginBottom: 20 },
                  getButtonStyle("NotificationTabStack"),
                ]}
                onPress={() => {
                  handleTabPress(AppScreens.NotificationMain);
                  handleNavigateToNotifications();
                }}
              >
                {renderButtonContent(
                  "NotificationTabStack",
                  AppImages.icNotificationBing,
                  "Notifications",
                  !isOnNotificationScreen,
                  totalNotificationCount
                )}
              </TouchableOpacity>
            </View>
          }
        />
      </View>
    </AuthProvider>
  );
}
